/**
 * useRecentSearches Hook
 * 
 * React hook for managing user's recent search history.
 * Now primarily uses Redux state with fallback to backend API calls.
 * 
 * @module useRecentSearches
 */

import { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { loadRecentSearchesRequest, saveRecentSearchRequest, addRecentSearch } from '../redux/recentSearches/recentSearchesSlice';
import userProfileService from '../services/userProfileService';

/**
 * Custom hook for managing recent searches
 * @param {Object} options - Configuration options
 * @param {boolean} [options.autoFetch=false] - Whether to automatically fetch on mount (now uses Redux state)
 * @param {number} [options.refreshInterval] - Auto-refresh interval in milliseconds
 * @param {boolean} [options.enableLocalFallback=true] - Enable localStorage fallback
 * @returns {Object} Hook state and methods
 */
export const useRecentSearches = ({
  autoFetch = false, // Changed default to false since Redux state is managed globally
  refreshInterval,
  enableLocalFallback = true
} = {}) => {
  // Get data from Redux store
  const dispatch = useDispatch();
  const { 
    recent: searches, 
    loading, 
    error, 
    lastFetch 
  } = useSelector((state) => state.recentSearches);

  // Get user from Redux store
  const user = useSelector((state) => state.user.user);
  const userId = user?.id;

  /**
   * Fetch recent searches from backend (fallback or force refresh)
   */
  const fetchSearches = useCallback(async (showLoading = true) => {
    if (!userId) {
      // If no user, try local fallback
      if (enableLocalFallback) {
        const localSearches = getLocalSearches();
        // Note: We can't directly set Redux state here, this is just for fallback
        console.log(`Found ${localSearches.length} local searches for fallback`);
      }
      return;
    }

    // Dispatch Redux action to load searches
    dispatch(loadRecentSearchesRequest());
  }, [userId, enableLocalFallback, dispatch]);

  /**
   * Save a new search (now uses Redux action)
   */
  const saveSearch = useCallback((searchData) => {
    if (!searchData) {
      return;
    }
    
    dispatch(saveRecentSearchRequest(searchData));
  }, [dispatch]);

  /**
   * Save a new search with immediate Redux update (for UI responsiveness)
   * Dispatches both addRecentSearch and saveRecentSearchRequest
   */
  const saveSearchWithImmediateUpdate = useCallback((searchData) => {
    if (!searchData) {
      return;
    }

    // Immediate Redux update for UI responsiveness
    dispatch(addRecentSearch(searchData));
    
    // Backend persistence
    dispatch(saveRecentSearchRequest(searchData));
  }, [dispatch]);

  /**
   * Refresh searches without showing loading state
   */
  const refreshSearches = useCallback(() => {
    fetchSearches(false);
  }, [fetchSearches]);

  /**
   * Get searches from localStorage (fallback)
   */
  const getLocalSearches = useCallback(() => {
    try {
      const localData = localStorage.getItem('recentSearchesV2');
      if (localData) {
        const parsed = JSON.parse(localData);
        return parsed.recent || [];
      }
    } catch (error) {
      console.error('useRecentSearches: Failed to parse local searches:', error);
    }
    return [];
  }, []);

  /**
   * Clear error state (not directly applicable with Redux, but kept for compatibility)
   */
  const clearError = useCallback(() => {
    // Could dispatch an action to clear error, but for now just log
    console.log('Clear error called - error state managed by Redux');
  }, []);

  /**
   * Force refresh with loading state
   */
  const forceRefresh = useCallback(() => {
    fetchSearches(true);
  }, [fetchSearches]);

  // Auto-fetch on mount or when userId changes (only if explicitly requested)
  useEffect(() => {
    if (autoFetch && userId && searches.length === 0 && !lastFetch) {
      // Only fetch if we don't have data yet and autoFetch is requested
      fetchSearches();
    }
  }, [autoFetch, userId, searches.length, lastFetch, fetchSearches]);

  // Set up auto-refresh interval (if needed)
  useEffect(() => {
    if (refreshInterval && refreshInterval > 0) {
      const interval = setInterval(refreshSearches, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [refreshInterval, refreshSearches]);

  return {
    searches,
    loading,
    error,
    lastFetch,
    fetchSearches,
    refreshSearches,
    clearError,
    forceRefresh,
    saveSearch, // Backend-only save
    saveSearchWithImmediateUpdate, // Immediate + backend save
    hasData: searches.length > 0,
    isEmpty: searches.length === 0 && !loading,
    isStale: lastFetch && (Date.now() - lastFetch) > 300000 // 5 minutes
  };
};
